import { Box, Button, CircularProgress } from '@mui/material';
import { InfoBar } from '../../shared/infoBar';
import { ErrorHeader } from '../../shared/errorHeader';
import { useEffect, useState } from 'react';
import { OrgSeason } from '../../api/models/orgSeason';
import { Team } from '../../api/models/team';
import { useNavigate, useParams } from 'react-router-dom';
import { Page, ProgramPageDefinition } from '../../models/programPageDefinition';
import { useApiCallWithAuth } from '../../hooks/useApiCallWithAuth';
import {
	createCustomProgramApi,
	generateCustomProgramApi,
	getSingleOrgSeasonApi,
	updateCustomProgramApi,
	updateCustomProgramOpponentLogoApi,
	updateCustomProgramSpecialSheetApi,
} from '../../api/orgSeasonApi';

import { CustomProgramUpdateCommand, ProgramOptions } from '../../api/models/program';

import { AddProgramPanel } from './addProgramPanel';
import { UpdateProgramPanel } from './updateProgramPanel';
import { PreviewProgramPanel } from './previewProgramPanel';
import { downloadBase64AsFile } from '../../utils/binaryHelper';

interface PageType {
	title: string;
	sheetType: 'image' | 'text';
}

const pages: PageType[] = [
	{ title: 'Front Cover', sheetType: 'image' },
	{ title: 'MVP Sponsor Page', sheetType: 'image' },
	{ title: 'All Star Sponsor Page', sheetType: 'image' },
	{ title: 'Back Cover', sheetType: 'image' },
];

export function CreateProgramPage() {
	const params = useParams();

	const [orgSeason, setOrgSeason] = useState<OrgSeason | null>(null);
	const [team, setTeam] = useState<Team | null>(null);

	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

	const [programOptions, setProgramOptions] = useState<ProgramOptions | undefined>(undefined);

	const [programPages, setProgramPages] = useState<Page[]>([]);
	const [selectedPage, setSelectedPage] = useState<Page | undefined>(undefined);

	const navigate = useNavigate();

	const getSingleOrgSeasonApiRequest = useApiCallWithAuth(getSingleOrgSeasonApi);
	const createCustomProgramApiRequest = useApiCallWithAuth(createCustomProgramApi);
	const updateCustomProgramApiRequest = useApiCallWithAuth(updateCustomProgramApi);
	const updateCustomProgramOpponentLogoApiRequest = useApiCallWithAuth(updateCustomProgramOpponentLogoApi);
	const updateCustomProgramSpecialSheetApiRequest = useApiCallWithAuth(updateCustomProgramSpecialSheetApi);
	const generateCustomProgramApiRequest = useApiCallWithAuth(generateCustomProgramApi);

	const navigateUrl =
		params.orgSeasonGuid && params.teamGuid
			? `/orgseason/${params.orgSeasonGuid}/team/${params.teamGuid}/program`
			: '';

	const onlineProgramUrl = params.programGuid ? `/program/online/${params.programGuid}` : '';

	useEffect(() => {
		initPage();
	}, []);


	async function initPage() {
		setIsLoading(true);

		try {
			if (params.orgSeasonGuid) {
				const orgSeasonData = await getSingleOrgSeasonApiRequest.request(params.orgSeasonGuid);
				setOrgSeason(orgSeasonData);

				if (orgSeasonData.teams && orgSeasonData.teams.length > 0) {
					const teamData = orgSeasonData.teams.find((team: Team) => team.id === params.teamGuid) as Team;
					setTeam(teamData);

					if (teamData && params.programGuid) {
						const programData = teamData.programOptions.find((po) => po.customProgramGuid === params.programGuid);

						if (programData) {
							setProgramOptions(programData);
							createProgramPages(teamData, programData);

						}
					}
				}
			}
		} catch (error) {
			setErrorMessage(`Failed to load organization season: ${error}`);
		}

		setIsLoading(false);
	}

	function createProgramPages(teamData:Team, programOptions:ProgramOptions){

		const numAthletes = teamData?.athletes ? teamData.athletes.length : 0;
        const numGames = teamData?.games ? teamData.games.length : 0;
        const numLeaders = teamData?.leaders ? teamData.leaders.length : 0;

        const pageDefinition = ProgramPageDefinition(numGames, numLeaders, numAthletes, programOptions);

		setProgramPages(pageDefinition);
	}

	async function createProgram(newProgramName: string): Promise<void> {
		setIsLoading(true);

		try {
			await createCustomProgramApiRequest.request(params.teamGuid, { name: newProgramName });
			navigate(navigateUrl);
		} catch (error) {
			setErrorMessage(`Failed to create program: ${error}`);
		}

		setIsLoading(false);
	}

	async function updateProgram(optionUpdates: CustomProgramUpdateCommand): Promise<void> {
		setIsLoading(true);

		try {
			await updateCustomProgramApiRequest.request(params.teamGuid, params.programGuid, optionUpdates);
			navigate(navigateUrl);
		} catch (error) {
			setErrorMessage(`Failed to create program: ${error}`);
		}

		setIsLoading(false);
	}

	async function updateProgramOpponentLogo(opponentLogo: string): Promise<void> {
		setIsLoading(true);

		try {
			await updateCustomProgramOpponentLogoApiRequest.request(params.teamGuid, params.programGuid, {
				image: opponentLogo,
			});
			navigate(navigateUrl);
		} catch (error) {
			setErrorMessage(`Failed to update opponent logo: ${error}`);
		}

		setIsLoading(false);
	}

	async function updateProgramSpecialSheet(pageIndex: number, specialSheet: string): Promise<void> {
		setIsLoading(true);

		// if (!selectedPage) {
		// 	setErrorMessage('No page selected for special sheet update');
		// 	setIsLoading(false);
		// 	return;
		// }

		try {
			const command = {
				image: specialSheet,
				customPageIndex: pageIndex,
			};

			await updateCustomProgramSpecialSheetApiRequest.request(params.teamGuid, params.programGuid, command);
			navigate(navigateUrl);
		} catch (error) {
			setErrorMessage(`Failed to update special sheet: ${error}`);
		}

		setIsLoading(false);
	}

		async function generateProgram(): Promise<void> {
		setIsLoading(true);

		try {
			const fileRequest = await generateCustomProgramApiRequest.request(params.teamGuid, params.programGuid);
			downloadBase64AsFile(fileRequest.data, fileRequest.name);

		} catch (error) {
			setErrorMessage(`Failed to create program: ${error}`);
		}

		setIsLoading(false);
	}

	
	//sets the selected page - that is the target of the updateProgramSheet
	function clickPage(idx: number) {
		const xSelectedPage = programPages[idx];
		if (xSelectedPage) {
			alert(`Selected page: ${xSelectedPage.title} - ${xSelectedPage.type} - ${xSelectedPage.startIndex}`);
			setSelectedPage(xSelectedPage);
		}
	}

	function mainContent() {
		return (
			<Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={4}>
				<Box
					p={3}
					borderRadius={3}
					boxShadow={2}
					bgcolor="background.paper"
					sx={{
						minHeight: 400,
						width: 300,
						background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
						border: '1px solid #e0e0e0',
					}}
				>
					{!params.programGuid && <AddProgramPanel onCreate={createProgram} />}
					{params.programGuid && programOptions && (
						<UpdateProgramPanel programOptions={programOptions} 
                                            onUpdate={updateProgram} 
                                            onChangeOpponentLogo={updateProgramOpponentLogo}
                                            onChangeSpecialSheet={updateProgramSpecialSheet}/>
					)}
				</Box>

				{programPages && programPages.length > 0 && (
					<Box
						flex={{ xs: '1 1 100%', md: '2 1 67%' }}
						p={3}
						borderRadius={3}
						boxShadow={2}
						bgcolor="background.paper"
						sx={{
							minHeight: 500,
							background: 'linear-gradient(135deg, #e0eafc 0%, #cfdef3 100%)',
							border: '1px solid #e0e0e0',
						}}
					>
						{<PreviewProgramPanel programPages={programPages} onClickPage={clickPage} />}
					</Box>
				)}
			</Box>
		);
	}

	return (
		<Box>
			<InfoBar navigateUrl={navigateUrl} />
			<ErrorHeader errorMessage={errorMessage} />
			{isLoading && <CircularProgress />}
			<h1>Create Program</h1>

			  <Box sx={{ display: 'flex', gap: 2 }}>
					<Button
						variant="contained"
						color="primary"
						onClick={generateProgram}
					>
						Generate Program
					</Button>

					<Button
						variant="contained"
						color="primary"
						 onClick={() => {
                            if (onlineProgramUrl) {
                                navigate(onlineProgramUrl);
                            } else {
                                setErrorMessage('Online program URL is not available.');
                            }
                        }}
					>
						View Online
					</Button>
				</Box>


			{team && mainContent()}
		</Box>
	);
}
